import { onBeforeUnmount, onMounted } from 'vue'

type UseAsyncPagePopupHistoryOptions = {
  /** Whether the popup is currently open. */
  isOpen: () => boolean
  /** Called when the visitor uses browser back/forward while the popup is open. */
  onNavigateAway: () => void
}

/**
 * Keeps the address bar in sync with the page shown inside an AsyncPagePopup.
 *
 * Opening *pushes* a history entry instead of replacing the current one, so the page the
 * popup was opened from keeps its own entry in the back stack. Without that, refreshing on
 * the popup url and then pressing back skips the underlying page: its entry had been
 * overwritten. Paging inside the popup rewrites that single entry, so closing stays one
 * step back.
 */
export const useAsyncPagePopupHistory = ({ isOpen, onNavigateAway }: UseAsyncPagePopupHistoryOptions) => {
  let urlBeforeOpen: string | null = null
  let hasPushedEntry = false

  /** Shows `url`: pushes an entry the first time, rewrites that entry while paging. */
  const showUrl = (url: string) => {
    // Pass the existing state along so vue-router's own bookkeeping (position, scroll)
    // stays intact on the entry we touch.
    if (hasPushedEntry) {
      window.history.replaceState(window.history.state, '', url)
      return
    }

    urlBeforeOpen = window.location.href
    window.history.pushState(window.history.state, '', url)
    hasPushedEntry = true
  }

  /**
   * Pops the pushed entry. For closes that keep the visitor on the page underneath (close
   * button, escape, overlay click), so that page keeps its place in the back stack.
   */
  const popUrl = () => {
    if (!hasPushedEntry) return

    hasPushedEntry = false
    window.history.back()
  }

  /**
   * Rewrites the pushed entry back to the url underneath. For closes that come with a
   * navigation: popping would race that navigation for the same history entry.
   */
  const restoreUrl = () => {
    if (!hasPushedEntry) return

    hasPushedEntry = false
    window.history.replaceState(window.history.state, '', urlBeforeOpen)
  }

  const onPopState = () => {
    if (!isOpen()) return

    // The visitor left our entry with back/forward, so the popup no longer matches the url:
    // close it and leave the history to the browser.
    hasPushedEntry = false
    onNavigateAway()
  }

  onMounted(() => window.addEventListener('popstate', onPopState))
  onBeforeUnmount(() => window.removeEventListener('popstate', onPopState))

  return { showUrl, popUrl, restoreUrl }
}
